/**
 * Build the load input `trace-viewer/cms` expects from a scene's content
 * rows.
 *
 * The auto-generated `SceneContentResponseDto` is wider and more optional
 * than the loader needs; this narrows it to `CmsContentInput` and settles
 * the one decision the app makes about a row: whether it is worth handing
 * to `findRootContent` / `contentDownloadUrl` at all.
 *
 * **`url` is passed through verbatim.** Under the folder content model
 * (CONTENT_MODEL_SPEC §2.1) a scene's `RootContent` row carries
 * `url = "<blob>/trace-content/scenes/{sceneId}/{sha256}.gltf"` — the
 * address of the document itself, with the folder its relative
 * `assets/…` URIs resolve against. Rewriting or re-deriving that URL
 * would lose the folder; `contentDownloadUrl` returns it unchanged and
 * only synthesizes a legacy `scene-content-files/{id}.{ext}` URL for
 * pre-`url` rows.
 *
 * **Nothing here keys on the file extension.** A `.gltf` row is an
 * ordinary scene document: the viewer sniffs its content to decide
 * between the document and single-asset paths, and the one
 * extension-keyed rule in the load path — the legacy `.zip` image-marker
 * frame conversion (ZIP_FRAME_CONTRACT) — lives inside `loadCmsScene`
 * and stays off for everything but `.zip`.
 */
import type { CmsContentInput } from 'trace-viewer/cms';

/** The fields of a `SceneContentResponseDto` row the loader can use. */
export interface SceneContentRow {
    id?: string | null;
    contentType?: string | null;
    name?: string | null;
    url?: string | null;
}

/**
 * Narrow scene content rows to the loader's input shape.
 *
 * Rows that carry neither a `url` nor an `id` are dropped: there is
 * nothing to download and `contentDownloadUrl` would throw on them. A row
 * with a `url` is kept even without an id — under the folder model the
 * URL is the whole address, and the id is only a fallback for
 * synthesizing the legacy one.
 */
export function toCmsContents(rows: readonly SceneContentRow[] | null | undefined): CmsContentInput[] {
    return (rows ?? []).flatMap(row => {
        const url = row.url ?? null;
        if (!row.id && !url) return [];
        return [{
            id: row.id ?? undefined,
            contentType: row.contentType ?? null,
            name: row.name ?? null,
            url,
        }];
    });
}
